import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { NAV } from "../constants/testIds";
import { LogOut, LayoutDashboard, Menu, X, Languages, User, Users } from "lucide-react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { lang, setLang, t } = useI18n();
    const [open, setOpen] = React.useState(false);

    const dashPath = (user?.role === "admin" || user?.role === "superadmin")
        ? "/admin"
        : user?.role === "teacher" ? "/teacher" : "/dashboard";
    // Staff (teacher/admin/superadmin) can also access the parent dashboard to
    // add and manage their own kids — expose it as a secondary nav link.
    const showParentView = user && user.role !== "parent";

    const linkCls = ({ isActive }) =>
        `text-sm font-medium tracking-wide transition-colors ${isActive ? "text-primary" : "text-brand-ink/70 hover:text-primary"}`;

    const langBtn = (
        <button
            type="button"
            data-testid="lang-toggle-btn"
            onClick={() => setLang(lang === "en" ? "ar" : "en")}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-border text-xs font-medium hover:bg-primary/5 transition"
            aria-label={t("language")}>
            <Languages className="w-3.5 h-3.5" /> {lang === "en" ? "AR" : "EN"}
        </button>
    );

    return (
        <header className="sticky top-0 z-50 glass">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
                    <div className="relative w-16 h-16 rounded-full overflow-hidden shadow-[0_6px_20px_-8px_rgba(122,26,40,0.55)] ring-1 ring-white/40 bg-white">
                        <img
                            src="/logo-circle.webp"
                            alt="Islamic Center of Federal Way"
                            className="w-full h-full object-contain transition-transform group-hover:scale-105"
                        />
                    </div>
                    <div className="leading-tight">
                        <div className="font-serif text-lg text-brand-ink">Islamic Center of Federal Way</div>
                        <div className="overline text-brand-stone">WEEKEND ISLAMIC SCHOOL</div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    <NavLink to="/" end className={linkCls} data-testid={NAV.home}>{t("nav_home")}</NavLink>
                    <NavLink to="/programs" className={linkCls} data-testid={NAV.programs}>{t("nav_programs")}</NavLink>
                    <NavLink to="/calendar" className={linkCls} data-testid={NAV.calendar}>{t("nav_calendar")}</NavLink>
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    {langBtn}
                    {!user && (
                        <>
                            <Button variant="ghost" onClick={() => navigate("/login")} data-testid={NAV.login} className="font-medium">
                                {t("nav_login")}
                            </Button>
                            <Button onClick={() => navigate("/signup")} data-testid={NAV.signup} className="bg-primary hover:bg-primary/90 font-medium">
                                {t("nav_signup")}
                            </Button>
                        </>
                    )}
                    {user && (
                        <>
                            <Button variant="ghost" onClick={() => navigate(dashPath)} data-testid={NAV.dashboard} className="gap-2 font-medium">
                                <LayoutDashboard className="w-4 h-4" /> {t("nav_dashboard")}
                            </Button>
                            {showParentView && (
                                <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="nav-parent-view-btn" className="gap-2 font-medium">
                                    <Users className="w-4 h-4" /> {t("nav_parent_view")}
                                </Button>
                            )}
                            <Button variant="ghost" onClick={() => navigate("/profile")} data-testid="nav-profile-btn" className="gap-2 font-medium">
                                <User className="w-4 h-4" /> {t("nav_profile")}
                            </Button>
                            <Button variant="outline" onClick={() => { logout(); navigate("/"); }} data-testid={NAV.logout} className="gap-2 font-medium">
                                <LogOut className="w-4 h-4" /> {t("nav_signout")}
                            </Button>
                        </>
                    )}
                </div>

                <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu">
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md">
                    <div className="px-6 py-4 flex flex-col gap-3">
                        <NavLink to="/" onClick={() => setOpen(false)} className="py-2">{t("nav_home")}</NavLink>
                        <NavLink to="/programs" onClick={() => setOpen(false)} className="py-2">{t("nav_programs")}</NavLink>
                        <NavLink to="/calendar" onClick={() => setOpen(false)} className="py-2">{t("nav_calendar")}</NavLink>
                        <div className="pt-2">{langBtn}</div>
                        {!user && <>
                            <Link to="/login" onClick={() => setOpen(false)} className="py-2">{t("nav_login")}</Link>
                            <Link to="/signup" onClick={() => setOpen(false)} className="py-2 font-semibold text-primary">{t("nav_signup")}</Link>
                        </>}
                        {user && <>
                            <Link to={dashPath} onClick={() => setOpen(false)} className="py-2">{t("nav_dashboard")}</Link>
                            {showParentView && (
                                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2">{t("nav_parent_view")}</Link>
                            )}
                            <Link to="/profile" onClick={() => setOpen(false)} className="py-2">{t("nav_profile")}</Link>
                            <button onClick={() => { logout(); navigate("/"); setOpen(false); }} className="py-2 text-left">{t("nav_signout")}</button>
                        </>}
                    </div>
                </div>
            )}
        </header>
    );
}
